import {useIntl, IntlShape} from 'react-intl';
import {Text, Heading, IconTooltip} from '@forecast-it/design-system';
import {ForecastTooltipFormulaRenderer} from '../../../../components/ForecastTooltipFormulaRenderer';
import React from 'react';
import styled from 'styled-components';
import Util from '../../util/util';

type TooltipItem = {
	title: string;
	details: string | unknown[];
};

const formatMoney = (value: number, currencySymbol: string | null | undefined, intl: IntlShape) => {
	return currencySymbol ? Util.getFormattedNumberWithCurrency(currencySymbol, value, intl) : value;
};

export const FinancialNumberGrid = styled.div.attrs({'data-financial-grid': ''})`
	display: grid;
	grid-template-columns: max-content 1fr;
	column-gap: 12px;
	row-gap: 16px;
	align-items: center;
	width: 100%;
`;

const HeroCell = styled.div.attrs({'data-financial-value': ''})`
	grid-column: 1 / -1;
`;

const HeroHeading = styled(Heading)<{$negative?: boolean}>`
	${({$negative, theme}) => $negative && `color: ${theme.sys.color.text.semantic.error};`}
`;

const ValueCell = styled.div.attrs({'data-financial-value': ''})``;

const LabelCell = styled.div``;

const InlineTooltip = styled.span`
	display: inline-block;
	vertical-align: middle;
`;

type HeroProps = {
	cy?: string;
	value: number;
	currencySymbol?: string;
	currency?: string;
};

export const FinancialNumberHero = ({cy, value, currencySymbol, currency}: HeroProps) => {
	const intl = useIntl();
	if (value === 0) {
		return null;
	}
	const theCurrencySymbol = currencySymbol || Util.GetCurrencySymbol(currency);
	return (
		<HeroCell data-testid={cy ? `${cy}-value` : undefined}>
			<HeroHeading size={4} color={'primary'} $negative={value < 0} noWrap={true} data-cy={cy}>
				{formatMoney(value, theCurrencySymbol, intl)}
			</HeroHeading>
		</HeroCell>
	);
};

type RowProps = {
	labelTranslationId?: string;
	label?: string;
	cy?: string;
	tooltipItem?: TooltipItem;
	value: number;
	currencySymbol?: string;
	currency?: string;
	zeroValuesHidden?: boolean;
};

export const FinancialNumberRow = ({
	labelTranslationId,
	label,
	cy,
	tooltipItem,
	value,
	currencySymbol,
	currency,
	zeroValuesHidden = false,
}: RowProps) => {
	const intl = useIntl();
	if (zeroValuesHidden && value === 0) {
		return null;
	}
	const theCurrencySymbol = currencySymbol || Util.GetCurrencySymbol(currency);
	const theLabel = label ? label : labelTranslationId ? intl.formatMessage({id: labelTranslationId}) : undefined;
	return (
		<>
			<ValueCell data-testid={cy ? `${cy}-value` : undefined}>
				<Text size={'2'} color={value < 0 ? 'error' : 'main'} noWrap={true} data-cy={cy}>
					{formatMoney(value, theCurrencySymbol, intl)}
				</Text>
			</ValueCell>

			{theLabel && (
				<LabelCell data-testid={cy ? `${cy}-label` : undefined}>
					<Text size={'2'} color={'auxiliary'}>
						{theLabel}
						{tooltipItem && (
							<>
								{' '}
								<InlineTooltip>
									{Array.isArray(tooltipItem.details) ? (
										<IconTooltip>
											<ForecastTooltipFormulaRenderer items={[tooltipItem]} translatedMessage={true} />
										</IconTooltip>
									) : (
										<IconTooltip>
											<IconTooltip.Item content={tooltipItem} />
										</IconTooltip>
									)}
								</InlineTooltip>
							</>
						)}
					</Text>
				</LabelCell>
			)}
		</>
	);
};
